'use client';

import { Suspense, useEffect, useRef } from 'react';

import { usePathname, useSearchParams } from 'next/navigation';
import Script from 'next/script';

function FacebookPixelInner({ id }: Readonly<{ id: string }>) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!id) return;

    // Skip the first PageView because it's handled by the script's initialization logic
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (typeof window !== 'undefined' && globalThis.fbq) {
      globalThis.fbq('track', 'PageView');
    }
  }, [id, pathname, searchParams]);

  return (
    <>
      <Script
        id="fb-pixel"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(globalThis, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');

            function getCookie(name) {
              const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
              return match ? match[2] : null;
            }
          
            const norm = {
              str: (v) => (v ?? '').toString().trim().toLowerCase(),
              email: (v) => (v ?? '').toString().trim().toLowerCase(),
              phone: (v) => (v ?? '').toString().replace(/\\D+/g, ''),
              name: (v) => (v ?? '').toString().trim().toLowerCase(),
              gender: (v) => {
                const g = (v ?? '').toString().trim().toLowerCase();
                if (g.startsWith('m')) return 'm';
                if (g.startsWith('f')) return 'f';
                return '';
              },
              dob: (v) => {
                if (!v) return '';
                const d = new Date(v);
                if (isNaN(d.getTime())) return '';
                const yyyy = d.getFullYear().toString().padStart(4, '0');
                const mm = (d.getMonth() + 1).toString().padStart(2, '0');
                const dd = d.getDate().toString().padStart(2, '0');
                return yyyy + mm + dd;
              },
              zip: (v) => (v ?? '').toString().replace(/\\s+/g, '').toLowerCase(),
              country: (v) => (v ?? '').toString().trim().toLowerCase(),
              state: (v) => (v ?? '').toString().trim().toLowerCase(),
              city: (v) => (v ?? '').toString().trim().toLowerCase(),
            };
          
            async function sha256Hex(text) {
              if (!text) return '';
              const enc = new TextEncoder();
              const data = enc.encode(text);
              const digest = await crypto.subtle.digest('SHA-256', data);
              const bytes = Array.from(new Uint8Array(digest));
              return bytes.map(b => b.toString(16).padStart(2, '0')).join('');
            }
          
            function readCustomerStore() {
              try {
                const raw = localStorage.getItem('customer-store');
                if (!raw) return null;
                const json = JSON.parse(raw);
                const user = json.state && json.state.customerData;
                if (!user) return null;
          
                const name = user.name || '';
                let firstName = '';
                let lastName  = '';
                if (name) {
                  const parts = name.trim().split(/\\s+/);
                  firstName = parts[0] || '';
                  lastName  = parts.length > 1 ? parts.slice(-1)[0] : '';
                }
          
                return {
                  email: user.email || '',
                  phone: user.phone || '',
                  firstName,
                  lastName,
                  birthDate: user.birthDate || user.dob || '',
                  externalId: user.customerId || ''
                };
              } catch (e) {
                return null;
              }
            }
          
            async function buildAdvancedMatching() {
              const fbp = getCookie('_fbp') || null;
              const fbc = getCookie('_fbc') || null;
          
              const guest = readCustomerStore();
              if (!guest) return { fbp, fbc };
          
              const email       = norm.email(guest.email);
              const phone       = norm.phone(guest.phone);
              const fn          = norm.name(guest.firstName);
              const ln          = norm.name(guest.lastName);
              const db          = norm.dob(guest.birthDate);
              const external_id = norm.str(guest.externalId);
          
              const [emH, phH, fnH, lnH, dbH, externalIdH] = await Promise.all([
                email ? sha256Hex(email) : '',
                phone ? sha256Hex(phone) : '',
                fn ? sha256Hex(fn) : '',
                ln ? sha256Hex(ln) : '',
                db ? sha256Hex(db) : '',
                external_id ? sha256Hex(external_id) : '',
              ]);
          
              const out = { fbp, fbc };
              if (emH) out.em = emH;
              if (phH) out.ph = phH;
              if (fnH) out.fn = fnH;
              if (lnH) out.ln = lnH;
              if (dbH) out.db = dbH;
              if (externalIdH) out.external_id = externalIdH;
          
              return out;
            }
          
            // Immediate activation to avoid "pixel has not been activated" warnings
            fbq('init', '${id}');
            
            (async function initPixel() {
              try {
                const advancedMatchingData = await buildAdvancedMatching();
                if (advancedMatchingData && Object.keys(advancedMatchingData).length > 2) {
                  // Re-init with advanced matching data (allowed to update user data)
                  fbq('init', '${id}', advancedMatchingData);
                }
              } catch (e) {
                // Fallback already covered by immediate init
              }
              // Fire the first PageView
              fbq('track', 'PageView');
            })();
          `,
        }}
      />
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: 'none' }}
          src={`https://www.facebook.com/tr?id=${id}&ev=PageView&noscript=1`}
        />
      </noscript>
    </>
  );
}

export function FacebookPixel({ id }: { id: string }) {
  if (!id) return null;

  return (
    <Suspense fallback={null}>
      <FacebookPixelInner id={id} />
    </Suspense>
  );
}
